import React, { useState, useEffect } from 'react';
import { Users, BookOpen, Layers, Tag, Calendar, Shield, Link, LayoutGrid } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import BusinessTypes from './BusinessTypes';
import ContentSizes from './ContentSizes';
import ContentTypes from './ContentTypes';
import Magazines from './Magazines';
import Schedules from './Schedules';
import UserManagement from './Users';
import CRMSettings from './CRMSettings';
import AdSizeSettings from './AdSizeSettings';
import { usePermissions } from '../hooks/usePermissions';
import PermissionGuard from '../components/PermissionGuard';

const Settings = () => {
  const location = useLocation();
  const { canAccessSettings, canManageUsers } = usePermissions();
  const [activeTab, setActiveTab] = useState('schedules');

  useEffect(() => {
    // Check if activeTab was passed in location state (from welcome modal)
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location]);
  // Ensure active tab is accessible

  const allTabs = [
    {
      id: 'schedules',
      name: 'Schedules',
      icon: Calendar,
      component: Schedules,
      permission: 'settings',
    },
    {
      id: 'magazines',
      name: 'Publications',
      icon: BookOpen,
      component: Magazines,
      permission: 'settings',
    },
    {
      id: 'business-types',
      name: 'Business Types',
      icon: Users,
      component: BusinessTypes,
      permission: 'settings',
    },
    {
      id: 'ad-sizes',
      name: 'Content Sizes',
      icon: LayoutGrid,
      component: AdSizeSettings,
      permission: 'settings',
    },
    {
      id: 'content-sizes',
      name: 'Rate Card',
      icon: Layers,
      component: ContentSizes,
      permission: 'settings',
    },
    {
      id: 'content-types',
      name: 'Content Types',
      icon: Tag,
      component: ContentTypes,
      permission: 'settings',
    },
    {
      id: 'crm',
      name: 'CRM Integration',
      icon: Link,
      component: CRMSettings,
      permission: 'settings',
    },
    {
      id: 'users',
      name: 'Users',
      icon: Shield,
      component: UserManagement,
      permission: 'manage-users',
    },
  ];

  // Filter tabs based on user permissions
  const tabs = allTabs.filter(tab => {
    if (tab.permission === 'settings') {
      return canAccessSettings();
    }
    if (tab.permission === 'manage-users') {
      // Only super-admin and admin can manage users
      return canManageUsers();
    }
    return true;
  });

  useEffect(() => {
    const hasAccessToActiveTab = tabs.some(tab => tab.id === activeTab);
    if (!hasAccessToActiveTab && tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  // Check if user has any accessible tabs
  if (tabs.length === 0) {
    return (
      <Layout>
        <div className="px-4 sm:px-6 lg:px-8">
          <PermissionGuard permission="settings" showFallback={true}>
            <div></div>
          </PermissionGuard>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage your application settings and configurations.
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">{ActiveComponent && <ActiveComponent />}</div>
      </div>
    </Layout>
  );
};

export default Settings;
